import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import { FormulaFieldEntity } from '../../../data-models/formula-field.data-model';
import { columnsRawUnwrapped } from '../../../services/state/AdminPanel/ColumnDefinitionsState';
import { formulaFieldsListLoadable } from '../../../services/state/AdminPanel/FormulaFieldsStateJ';
import { quantitativeKpiConfigUnwrapped } from '../../../services/state/KPIConfigState';
import { buildFormulaVocabulary } from '../../../util/formula-vocabulary';
import { IFormFieldFormulaMeta } from '../../../view-models/form.view-model';
import { FormulaEditor } from '../../FormulaEditor/FormulaEditor';
import {
  chipTargets,
  FormulaSerialized,
  segmentCanonicalFormula,
  serializeSegments,
} from '../../FormulaEditor/serialization';
import { useSurfacedValidation, validateFormula } from '../../FormulaEditor/validateFormula';
import { IBaseFieldProps } from './types';

/**
 * Neither shortcut is discoverable — the editor looks like a plain text box — so the helper
 * line advertises them whenever it has nothing more pressing to say. Ctrl+Space only:
 * `HintOnDemandPlugin` ignores the event while Cmd is held, and on macOS Cmd+Space belongs
 * to Spotlight.
 */
const SHORTCUT_HINT = 'Press Ctrl+Space to see available functions and variables, or [ to see variables only';

/**
 * Form-factory renderer for Excel-style formulas (RendererType.formula).
 * The react-hook-form value is the CANONICAL formula text (bracketed row
 * paths — the storage form); the display rendering (bracketed display paths),
 * the referenceable vocabulary, and the editor-grade validation UX (debounced
 * surfacing, inline underline, typeahead-hold) all stay internal. Like
 * FieldCompanySearch, the renderer reads its app state (the columns
 * vocabulary) itself — rendererMeta cannot carry derived Maps. The form's
 * watched `entity` value scopes the vocabulary; forms without an entity field
 * get the company vocabulary (the pre-entity behavior).
 */
export function FieldFormula(props: IBaseFieldProps<unknown>) {
  const { formField, formProps } = props;
  const columnsMeta = useAtomValue(columnsRawUnwrapped);
  const formulaLoad = useAtomValue(formulaFieldsListLoadable);
  const kpis = useAtomValue(quantitativeKpiConfigUnwrapped);
  const excludeKey = (formField.rendererMeta as IFormFieldFormulaMeta | undefined)?.excludeKey;
  const entity = (useWatch({ name: 'entity' }) as FormulaFieldEntity | undefined) ?? 'company';
  const vocabulary = useMemo(() => {
    const formulaFields = formulaLoad.state === 'hasData' ? (formulaLoad.data ?? []) : [];
    return buildFormulaVocabulary(columnsMeta, formulaFields, { entity, excludeKey, kpis });
  }, [columnsMeta, formulaLoad, entity, excludeKey, kpis]);
  const targets = useMemo(() => chipTargets(vocabulary.referenceable), [vocabulary]);
  // The editor navigates the records and references the fields.
  const editorFields = useMemo(() => [...vocabulary.records, ...vocabulary.referenceable], [vocabulary]);

  // The editor owns its content after mount; capture the initial canonical once.
  const initialFormulaRef = useRef((formProps.value as string | undefined) || undefined);
  const [serialized, setSerialized] = useState<FormulaSerialized>(() =>
    serializeSegments(segmentCanonicalFormula(initialFormulaRef.current ?? '', targets))
  );

  // An entity switch (create mode only — entity is locked on edit) invalidates
  // every chip, so the formula resets: clear the content during render, before
  // the keyed editor below remounts against the new vocabulary.
  const [renderedEntity, setRenderedEntity] = useState(entity);
  if (renderedEntity !== entity) {
    setRenderedEntity(entity);
    initialFormulaRef.current = undefined;
    setSerialized(serializeSegments(segmentCanonicalFormula('', targets)));
  }

  const validation = useMemo(() => validateFormula(serialized, vocabulary), [serialized, vocabulary]);
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const surfaced = useSurfacedValidation(validation, typeaheadOpen);
  // An unfinished formula is work in progress, not a mistake: it keeps the
  // focused border and neutral helper text, and says what it is still missing
  // without the red. A Save attempt ends that grace — react-hook-form only
  // writes this field's error on submit, and clears it again once the formula
  // is finished — and so does anything genuinely wrong (unknown field, type
  // mismatch, circular reference), which is never `incomplete`.
  const { errors } = useFormState({ name: formProps.name });
  const saveAttempted = errors[formProps.name] != null;
  const unfinished = surfaced.state === 'error' && surfaced.incomplete === true && !saveAttempted;
  const surfacedError = surfaced.state === 'error' && !unfinished ? surfaced : null;
  const inProgressMessage = surfaced.state === 'error' && unfinished ? surfaced.message : null;
  // What the form's own rule found on submit — a result type that does not match the chosen
  // Type, a circular reference — once the formula itself is valid.
  const ruleError = errors[formProps.name]?.message;
  const ruleMessage = typeof ruleError === 'string' && ruleError ? ruleError : null;

  const { onChange } = formProps;
  // The render-time reset above cannot touch react-hook-form state; sync the
  // cleared value after the switch commits.
  const syncedEntityRef = useRef(entity);
  useEffect(() => {
    if (syncedEntityRef.current === entity) {
      return;
    }
    syncedEntityRef.current = entity;
    onChange('');
  }, [entity, onChange]);

  const handleChange = useCallback(
    (next: FormulaSerialized) => {
      setSerialized(next);
      onChange(next.canonical);
    },
    [onChange]
  );

  return (
    <FormulaEditor
      key={entity}
      fields={editorFields}
      initialFormula={initialFormulaRef.current}
      onChange={handleChange}
      label={formField.label ?? 'Formula'}
      showLabel={false}
      placeholder={formField.placeholder ?? 'e.g. IF([Metrics.IRR] > 0.2, [Metrics.FMV] * 2, [Metrics.FMV])'}
      error={surfacedError != null || ruleMessage != null}
      errorRange={surfacedError?.range ?? null}
      onTypeaheadOpenChange={setTypeaheadOpen}
      onFocusChange={setFocused}
      helperText={
        // Anything wrong with the formula outranks the hint; the rest of the line is
        // feedback on what is being written, so it only applies while writing it.
        surfacedError?.message ??
        ruleMessage ??
        inProgressMessage ??
        (!focused
          ? SHORTCUT_HINT
          : validation.state === 'ok'
            ? `References: ${validation.referenced.join(', ') || 'none'}`
            : 'Type [ to pick a record, then . to reach its fields')
      }
    />
  );
}
